import {
	COMPANIES_CHANGED, FOLDER_GET, FOLDER_GET_SUCCESS, FOLDER_GET_FAIL, FOLDER_SET_PARENT,
	REMOVE_COMPANY_FOLDER,
	REMOVE_COMPANY_FOLDER_SUCCESS,
	REMOVE_COMPANY_FOLDER_FAIL,
	FOLDER_ADD,
	FOLDER_ADD_SUCCESS,
	FOLDER_ADD_FAIL,
	FOLDER_DELETE,
	FOLDER_DELETE_SUCCESS,
	FOLDER_DELETE_FAIL,
	FOLDER_UPDATE,
	FOLDER_UPDATE_SUCCESS,
	FOLDER_UPDATE_FAIL
} from './actions';
import { HTTP_API_URL } from 'config';
import clientId from '../../utils/clientid';
import { fetchSession } from './session';
import { changeCompanyAndNotify } from './companies';
import { extractPropsforSuperagentHttpError } from '../../errors/errorhelper';
import ERRORS from '../../errors/errorids';
import AppError from '../../errors/apperror';
import HttpError from '../../errors/httperror';

export const initialState = {
	cloudProviders: [],
	loading: false,
	folders: {},
	parents: {}
};

function toCloudStore(id) {
	const [name] = id.split(':');

	return {
		providerId: id,
		providerName: name
	};
}

function sortByFolderName(first, second) {
	return first.filename.localeCompare(second.filename);
}

function getFolderData(cloudFolders, fileId, providerId) {
	return cloudFolders.folders[fileId + providerId];
}

function getTitle(folder, providerId) {
	return folder.fileId === 'root' ? providerId.split(':')[0] : folder.title;
}

export default (state = initialState, action) => {
	switch (action.type) {
		case COMPANIES_CHANGED: {
			const cloudstore = action.result.company.cloudstore.map(toCloudStore);

			return {
				...state,
				cloudProviders: cloudstore
			};
		}
		case FOLDER_GET_FAIL: {
			return {
				...state,
				loading: false,
			};
		}
		case FOLDER_GET:
			return {
				...state,
				loading: true,
			};
		case FOLDER_SET_PARENT:
			return {
				...state,
				parents: {
					...state.parents,
					[action.folderId]: action.parentFolder
				}
			};
		case FOLDER_GET_SUCCESS: {
			const contents = action.result.contents
				.filter(content => content.isDirectory)
				.sort(sortByFolderName);

			return {
				...state,
				loading: false,
				folders: {
					...state.folders,
					[action.result.directory.fileId + action.result.directory.providerId]: {
						...action.result.directory,
						contents,
					}
				}
			};
		}
		default:
			return state;
	}

};

export const changeFolder = (fileId, providerId, parentFolder) => {
	return dispatch => {
		dispatch({ type: FOLDER_SET_PARENT, folderId: fileId + providerId, parentFolder });
		providerId = encodeURIComponent(providerId);

		return dispatch({
			types: [FOLDER_GET, FOLDER_GET_SUCCESS, FOLDER_GET_FAIL],
			promise: client => client.get(`/files?fileid=${fileId}&providerid=${providerId}`).catch((error) => {
				const httpErrorProps = extractPropsforSuperagentHttpError(error);

				const nError = new AppError({
					message: 'cloudFolders.changeFolder() failed',
					errorId: ERRORS.UNKNOWN,
					originalError: new HttpError(httpErrorProps),
					visualisers: { feature: 'REDUX_CLOUD_FOLDERS' }
				});

				throw nError;
			})
		});
	};
};

export const updateFolder = ({providerId, fileId, type, name}, companyId) => {
	return dispatch => {
		return dispatch({
			types: [FOLDER_UPDATE, FOLDER_UPDATE_SUCCESS, FOLDER_UPDATE_FAIL],
			promise: client => client.patch(`/companies/${companyId}/folders`, {
				data: {
					providerId,
					fileId,
					type,
					name
				}
			}).catch((error) => {
				const httpErrorProps = extractPropsforSuperagentHttpError(error);

				const nError = new AppError({
					message: 'cloudFolders.updateFolder() failed',
					errorId: ERRORS.UNKNOWN,
					originalError: new HttpError(httpErrorProps),
					visualisers: { feature: 'REDUX_CLOUD_FOLDERS' }
				});

				throw nError;
			})
		});
	};
};

export const removeFolder = ({ companyId, providerId, fileId, type }) => {
	return dispatch => {
		return dispatch({
			types: [FOLDER_DELETE, FOLDER_DELETE_SUCCESS, FOLDER_DELETE_FAIL],
			promise: client => client.delete(`/companies/${companyId}/folders`, {
				data: {
					fileId,
					type,
					providerId
				}
			}).catch((error) => {
				const httpErrorProps = extractPropsforSuperagentHttpError(error);

				const nError = new AppError({
					message: 'cloudFolders.removeFolder() failed',
					errorId: ERRORS.UNKNOWN,
					originalError: new HttpError(httpErrorProps),
					visualisers: { feature: 'REDUX_CLOUD_FOLDERS' }
				});

				throw nError;
			})
		});
	};
};

/**
 * Get folder breadcrumb.
 *
 * @param {Object} cloudFolders
 * @param {Object} currentFolder
 *
 * @returns {Array}
 */
export const getFolderPaths = (cloudFolders, currentFolder) => {
	const path = [];

	let parent = getFolderData(cloudFolders, currentFolder.fileId, currentFolder.providerId);

	while (parent) {
		path.push({
			id: parent.fileId,
			displayName: getTitle(parent, currentFolder.providerId)
		});

		parent = cloudFolders.parents[parent.fileId + currentFolder.providerId];

		if (parent) {
			parent = getFolderData(cloudFolders, parent.fileId, parent.providerId);
		}
	}

	return path;
};

export const addFolder = ({ companyId, providerId, fileId, type, currentFolder }) => {
	return (dispatch, getState) => {
		const { cloudFolders } = getState();
		const path = getFolderPaths(cloudFolders, currentFolder);
		return dispatch({
			types: [FOLDER_ADD, FOLDER_ADD_SUCCESS, FOLDER_ADD_FAIL],
			promise: client => client.post(`/companies/${companyId}/folders`, {
				data: {
					fileId,
					type,
					providerId,
					path: path.reverse()
				}
			}).catch((error) => {
				const httpErrorProps = extractPropsforSuperagentHttpError(error);

				const nError = new AppError({
					message: 'cloudFolders.addFolder() failed',
					errorId: ERRORS.UNKNOWN,
					originalError: new HttpError(httpErrorProps),
					visualisers: { feature: 'REDUX_CLOUD_FOLDERS' }
				});

				throw nError;
			})
		});
	};
};

export function authenticateProvider(name, companyId) {
	const location = encodeURIComponent(window.location.href);
	const authUrl = `${HTTP_API_URL}/auth/${name}`;
	const qs = `?host=${location}&auth-target=company&company-id=${companyId}&auth-action=store-provider&clientid=${clientId}`;

	window.location = authUrl + qs;
}

export function removeProvider(providerId, companyId) {
	return dispatch => {
		return dispatch({
			types: [REMOVE_COMPANY_FOLDER, REMOVE_COMPANY_FOLDER_SUCCESS, REMOVE_COMPANY_FOLDER_FAIL],
			promise: client => client.patch(`/companies/${companyId}/providers`, {
				data: { providerIds: [providerId] }
			})
				.then(() => dispatch(fetchSession()))
				.then(() => dispatch(changeCompanyAndNotify()))
				.catch((error) => {
					const httpErrorProps = extractPropsforSuperagentHttpError(error);

					const nError = new AppError({
						message: 'cloudFolders.removeProvider() failed',
						errorId: ERRORS.UNKNOWN,
						originalError: new HttpError(httpErrorProps),
						visualisers: { feature: 'REDUX_CLOUD_FOLDERS' }
					});

					throw nError;
				})				
		});
	};
}



// WEBPACK FOOTER //
// ./src/redux/modules/cloudFolders.js